// Heart Games Service
// Handles game data fetching and session management

import { HeartGame, GameSession, GameAnswer, GameResult } from '../../types/heartGames';
import { config } from '../../config/env';
import { authInterceptor } from '../Authentication/authInterceptor';
import { signalsService } from '../SocialFeed/signalsService';

const API_BASE = `${config.api.baseUrl}/heart-games`;

function headers(json = true): HeadersInit {
  const h: HeadersInit = { Authorization: `Bearer ${authInterceptor.getAccessToken() || ''}` };
  if (json) h['Content-Type'] = 'application/json';
  return h;
}

async function unwrap(response: Response): Promise<any> {
  if (!response.ok) throw new Error(`Request failed: ${response.statusText}`);
  const body = await response.json();
  return body.data;
}

/** A real person who has played, as returned by /heart-games/compatible-users. */
export interface CompatibleCandidate {
  userId: string;
  name: string;
  age: number | null;
  location: string;
  bio: string;
  photo: string | null;
  mbtiType: string;
  primaryPersona: string | null;
}

export class GameService {
  // Mock data for development
  // Only Heart Choices game for now - other games will be added later
  private static mockGames: HeartGame[] = [
    {
      id: 'heart-choices-1',
      name: 'Heart Choices',
      description: 'Answer questions about dating and relationships to discover your personality',
      type: 'scenario',
      category: 'dating',
      questions: [],
      scoring: {
        totalQuestions: 25,
        weights: { E: 1, I: 1, S: 1, N: 1, T: 1, F: 1, J: 1, P: 1 },
        thresholds: { strong: 7, moderate: 5, weak: 3 }
      },
      duration: 600, // 10 minutes (increased for more questions)
      difficulty: 'easy',
      minQuestions: 5,
      maxQuestions: 25,
      rewards: [
        { type: 'points', value: 100, description: 'Complete Heart Choices' },
        { type: 'badge', value: 'Decision Maker', description: 'Finished scenario game' }
      ],
      icon: '💝',
      color: '#ff6b6b'
    },
    {
      id: 'moments-challenge',
      name: 'Moments Challenge',
      description: 'Share your daily Moments to boost discoverability! Add a Moment from your profile to participate.',
      type: 'challenge',
      category: 'social',
      questions: [],
      scoring: {
        totalQuestions: 1,
        weights: {},
        thresholds: { strong: 1, moderate: 0, weak: 0 }
      },
      duration: 0,
      difficulty: 'easy',
      minQuestions: 0,
      maxQuestions: 0,
      rewards: [
        { type: 'boost', value: 24, description: '24h Profile Boost' },
        { type: 'badge', value: 'Social Star', description: 'Shared first moment' }
      ],
      icon: '📸',
      color: '#4ECDC4'
    }
    // TODO: Add other games later
    // Heart Sparks, Heart Visions, Heart Bingo, Heart Adventures, Heart Sync
  ];

  private static mockSessions: GameSession[] = [];

  /**
   * Get all available Heart Games
   */
  static async getGames(): Promise<HeartGame[]> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return [...this.mockGames];
    } catch (error) {
      console.error('Error fetching games:', error);
      throw new Error('Failed to fetch games');
    }
  }

  /**
   * Get a specific game by ID
   */
  static async getGame(gameId: string): Promise<HeartGame> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const game = this.mockGames.find(g => g.id === gameId);
      if (!game) {
        throw new Error(`Game with ID ${gameId} not found`);
      }
      
      return { ...game };
    } catch (error) {
      console.error('Error fetching game:', error);
      throw new Error('Failed to fetch game');
    }
  }

  /**
   * Start a new game session
   */
  static async startGameSession(gameId: string, userId: string): Promise<GameSession> {
    try {
      const response = await fetch(`${API_BASE}/sessions`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ gameId }),
      });
      const data = await unwrap(response);
      signalsService.track({ signalType: 'game_start', surface: 'heart_games', itemId: gameId });
      return {
        id: data.id, userId: data.user_id, gameId: data.game_id,
        startedAt: new Date(data.started_at), answers: data.answers ?? [], isCompleted: data.is_completed,
      };
    } catch (error) {
      console.error('Error starting game session, falling back to local session:', error);
      const game = this.mockGames.find(g => g.id === gameId);
      if (!game) throw new Error(`Game with ID ${gameId} not found`);
      const session: GameSession = { id: `session-${Date.now()}`, userId, gameId, startedAt: new Date(), answers: [], isCompleted: false };
      this.mockSessions.push(session);
      return { ...session };
    }
  }

  /**
   * Submit answers for a game session
   */
  static async submitAnswers(sessionId: string, answers: GameAnswer[]): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/sessions/${sessionId}/answers`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({ answers }),
      });
      await unwrap(response);
    } catch (error) {
      console.error('Error submitting answers, falling back to local session:', error);
      const session = this.mockSessions.find(s => s.id === sessionId);
      if (session) session.answers = [...answers];
    }
  }

  /**
   * Complete a game session
   */
  static async completeGameSession(sessionId: string, result: GameResult): Promise<void> {
    try {
      const response = await fetch(`${API_BASE}/sessions/${sessionId}/complete`, {
        method: 'PUT',
        headers: headers(),
        body: JSON.stringify({
          score: result.score,
          duration: result.duration,
          insights: result.insights,
          recommendations: result.recommendations,
        }),
      });
      await unwrap(response);
      // No target: a finished game describes the player, and it is the
      // personality data the compatibility side of matching reads.
      signalsService.track({
        signalType: 'game_complete',
        surface: 'heart_games',
        itemId: sessionId,
        durationMs: result.duration ? result.duration * 1000 : undefined,
        metadata: { score: result.score ?? null },
      });
    } catch (error) {
      console.error('Error completing game session, falling back to local session:', error);
      const session = this.mockSessions.find(s => s.id === sessionId);
      if (session) {
        session.completedAt = new Date();
        session.duration = result.duration;
        session.score = result.score;
        session.isCompleted = true;
      }
    }
  }

  /**
   * Get game session by ID
   */
  static async getGameSession(sessionId: string): Promise<GameSession | null> {
    try {
      const response = await fetch(`${API_BASE}/sessions/${sessionId}`, { headers: headers(false) });
      const data = await unwrap(response);
      if (!data) return null;
      return {
        id: data.id, userId: data.user_id, gameId: data.game_id,
        startedAt: new Date(data.started_at), completedAt: data.completed_at ? new Date(data.completed_at) : undefined,
        duration: data.duration, score: data.score, answers: data.answers ?? [], isCompleted: data.is_completed,
      };
    } catch (error) {
      console.error('Error fetching game session, falling back to local session:', error);
      const session = this.mockSessions.find(s => s.id === sessionId);
      return session ? { ...session } : null;
    }
  }

  /**
   * The user's most recent session — for one game (resume support), or their
   * latest COMPLETED session of any game when no gameId is given.
   *
   * The second form is what the standalone Heart Profile page needs: it is
   * reached from a profile badge, so it has no game in hand and only wants
   * whatever result the user last earned.
   *
   * Returns null if there is nothing to find.
   */
  static async getLatestSession(gameId?: string): Promise<GameSession | null> {
    try {
      const query = gameId ? `?gameId=${encodeURIComponent(gameId)}` : '';
      const response = await fetch(`${API_BASE}/sessions/latest${query}`, {
        headers: headers(false),
      });
      const data = await unwrap(response);
      if (!data) return null;
      return {
        id: data.id, userId: data.user_id, gameId: data.game_id,
        startedAt: new Date(data.started_at), completedAt: data.completed_at ? new Date(data.completed_at) : undefined,
        duration: data.duration, score: data.score, answers: data.answers ?? [], isCompleted: data.is_completed,
      };
    } catch (error) {
      // No resume available (e.g. offline) — caller will start fresh.
      console.error('Error fetching latest session:', error);
      return null;
    }
  }

  /**
   * Real people who have completed a Heart Game, with the MBTI type they
   * earned. Unscored on purpose: the caller ranks them with ScoringService,
   * so the compatibility rule has exactly one implementation.
   */
  static async getCompatibleUsers(limit = 50): Promise<CompatibleCandidate[]> {
    const response = await fetch(`${API_BASE}/compatible-users?limit=${limit}`, {
      headers: headers(false),
    });
    const data = await unwrap(response);
    return (data?.candidates ?? []) as CompatibleCandidate[];
  }

  /**
   * Get user's game history
   */
  static async getUserGameHistory(userId: string): Promise<GameResult[]> {
    try {
      const response = await fetch(`${API_BASE}/history`, { headers: headers(false) });
      const data = await unwrap(response);
      return (data ?? []).map((row: any) => ({
        gameId: row.game_id, userId: row.user_id, score: row.score,
        answers: [], duration: row.duration, completedAt: new Date(row.completed_at),
        insights: row.insights ?? [], recommendations: row.recommendations ?? [],
      }));
    } catch (error) {
      console.error('Error fetching game history, falling back to empty history:', error);
      return [];
    }
  }

  /**
   * Get games by category
   */
  static async getGamesByCategory(category: string): Promise<HeartGame[]> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      return this.mockGames.filter(game => game.category === category);
    } catch (error) {
      console.error('Error fetching games by category:', error);
      throw new Error('Failed to fetch games by category');
    }
  }

  /**
   * Get games by difficulty
   */
  static async getGamesByDifficulty(difficulty: string): Promise<HeartGame[]> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      return this.mockGames.filter(game => game.difficulty === difficulty);
    } catch (error) {
      console.error('Error fetching games by difficulty:', error);
      throw new Error('Failed to fetch games by difficulty');
    }
  }

  /**
   * Search games by name or description
   */
  static async searchGames(query: string): Promise<HeartGame[]> {
    try {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const lowercaseQuery = query.toLowerCase();
      return this.mockGames.filter(game => 
        game.name.toLowerCase().includes(lowercaseQuery) ||
        game.description.toLowerCase().includes(lowercaseQuery)
      );
    } catch (error) {
      console.error('Error searching games:', error);
      throw new Error('Failed to search games');
    }
  }
}









